import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ReferralSection } from "../components/dashboard/ReferralSection";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { LegalDataCard } from "../components/LegalDataCard";
import { DashboardShell } from "../components/dashboard/DashboardShell";
import { WebsiteEditor } from "../components/dashboard/WebsiteEditor";
import { SeoSection } from "../components/dashboard/SeoSection";
import { UpgradeDialog } from "../components/dashboard/UpgradeDialog";
import { BookingSection } from "../components/dashboard/BookingSection";
import { ShopSection } from "../components/dashboard/ShopSection";
import { BrandingSection } from "../components/dashboard/BrandingSection";
import { proxyPageImages } from "../lib/imageProxy";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { 
  ArrowLeft, Eye, Users, Mail, Phone, MessageSquare, 
  Calendar, Loader2, Layout, ExternalLink, RefreshCw,
  Send, FileText, Image, PaintBucket, HelpCircle, 
  Clock, CheckCircle2, Package, CreditCard,
  Globe, Shield, Server, Rocket, AlertCircle, XCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUEST_TYPES = [
  { id: "text", label: "Text-Änderung", icon: FileText },
  { id: "image", label: "Bild-Änderung", icon: Image },
  { id: "layout", label: "Layout-Anpassung", icon: PaintBucket },
  { id: "other", label: "Sonstiges", icon: HelpCircle },
];

const STATUS_MAP = {
  open: { label: "Offen", color: "bg-blue-100 text-blue-700" },
  review: { label: "KI-Vorschlag bereit", color: "bg-amber-100 text-amber-700" },
  completed: { label: "Live", color: "bg-green-100 text-green-700" },
  rejected: { label: "Abgelehnt", color: "bg-red-100 text-red-700" },
};

const PROV_STEPS = [
  { key: "payment", label: "Zahlung", Icon: CreditCard },
  { key: "domain_purchase", label: "Domain-Kauf", Icon: Globe },
  { key: "dns_setup", label: "DNS-Setup", Icon: Server },
  { key: "nameserver_update", label: "Nameserver", Icon: RefreshCw },
  { key: "ssl_certificate", label: "SSL-Zertifikat", Icon: Shield },
  { key: "site_live", label: "Live", Icon: Rocket },
];

function ProvisioningPipeline({ provisioning, domainType, allDone }) {
  if (!provisioning || Object.keys(provisioning).length === 0) return null;
  if (allDone) return null;

  // Filter steps relevant to the domain type
  const steps = PROV_STEPS.filter(s => {
    const st = provisioning[s.key];
    if (!st) return false;
    // Hide skipped steps for cleaner UI
    if (st.status === "skipped") return false;
    return true;
  });

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 sm:p-6 mb-6" data-testid="provisioning-pipeline">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
        <h2 className="text-sm font-semibold text-slate-700">Domain wird eingerichtet</h2>
      </div>

      <div className="flex items-center gap-0 overflow-x-auto pb-2">
        {steps.map((step, i) => {
          const st = provisioning[step.key] || {};
          const isDone = st.status === "completed";
          const isProcessing = st.status === "processing";
          const isFailed = st.status === "failed";
          const isPending = st.status === "pending" || st.status === "pending_approval";
          const Icon = step.Icon;

          return (
            <div key={step.key} className="flex items-center flex-shrink-0">
              <div className="flex flex-col items-center gap-1.5">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  isDone ? "bg-emerald-100 text-emerald-600" :
                  isProcessing ? "bg-indigo-100 text-indigo-600" :
                  isFailed ? "bg-red-100 text-red-600" :
                  "bg-slate-100 text-slate-400"
                }`}>
                  {isDone ? <CheckCircle2 className="w-5 h-5" /> :
                   isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> :
                   isFailed ? <AlertCircle className="w-5 h-5" /> :
                   <Icon className="w-4 h-4" />}
                </div>
                <span className={`text-[11px] font-medium whitespace-nowrap ${
                  isDone ? "text-emerald-600" :
                  isProcessing ? "text-indigo-600" :
                  isFailed ? "text-red-600" :
                  "text-slate-400"
                }`}>{step.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`w-8 sm:w-12 h-0.5 mx-1 mb-5 ${
                  isDone ? "bg-emerald-300" : "bg-slate-200"
                }`} />
              )}
            </div>
          );
        })}
      </div>

      {provisioning.domain_purchase?.status === "pending_approval" && (
        <p className="text-xs text-amber-600 mt-3 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          Deine Domain wird von unserem Team geprüft. Du erhältst eine E-Mail, sobald es weitergeht.
        </p>
      )}
    </div>
  );
}

function SiteLiveBanner({ domainName, domainType }) {
  const url = domainType === "subdomain" 
    ? `https://${domainName}.webgenerator.io` 
    : `https://${domainName}`;
  return (
    <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-5 sm:p-6 mb-6 text-white" data-testid="site-live-banner">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
            <Rocket className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-bold text-lg">Deine Seite ist live!</h2>
            <p className="text-white/80 text-sm">{url}</p>
          </div>
        </div>
        <Button
          data-testid="visit-live-site"
          onClick={() => window.open(url, "_blank")}
          className="bg-white text-emerald-700 hover:bg-white/90 rounded-full h-10 px-5 text-sm font-semibold"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Seite öffnen
        </Button>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { pageId } = useParams();
  const [page, setPage] = useState(null);
  const [leads, setLeads] = useState([]);
  const [retention, setRetention] = useState({ lead_max_days: 365, lead_processed_days: 30 });
  const [stats, setStats] = useState({ visitor_count: 0, lead_count: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Provisioning
  const [provStatus, setProvStatus] = useState(null);
  const pollRef = useRef(null);

  // Content Requests
  const [contentRequests, setContentRequests] = useState([]);
  const [updatesRemaining, setUpdatesRemaining] = useState(0);
  const [updatesTotal, setUpdatesTotal] = useState(0);
  const [packageName, setPackageName] = useState("Kein Abo");
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestType, setRequestType] = useState("text");
  const [requestDesc, setRequestDesc] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get("tab") || "overview";
  const setSection = (id) => setSearchParams({ tab: id });
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const previewPage = useMemo(() => proxyPageImages(page), [page]);

  // Cancellation
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchData();
    fetchProvisioningStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [pageId]);

  // Auto-poll provisioning every 15s while not all done
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (provStatus && (!provStatus.all_done || !provStatus.live_verified)) {
      pollRef.current = setInterval(fetchProvisioningStatus, 15000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [provStatus?.all_done, provStatus?.live_verified]);

  const fetchProvisioningStatus = async () => {
    try {
      const res = await axios.get(`${API}/pages/${pageId}/provisioning-status`);
      setProvStatus(res.data);
    } catch {
      // silently fail - page might not have provisioning yet
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [pageRes, leadsRes, statsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/pages/${pageId}`),
        axios.get(`${API}/leads/${pageId}`, { headers }),
        axios.get(`${API}/pages/${pageId}/stats`),
        axios.get(`${API}/content-requests/${pageId}`).catch(() => ({ data: { requests: [], updates_remaining: 0, updates_total: 0, package_name: "Kein Abo" } })),
      ]);

      setPage(pageRes.data);
      setLeads(leadsRes.data.leads || []);
      if (leadsRes.data.retention) setRetention(leadsRes.data.retention);
      setStats(statsRes.data);
      setContentRequests(requestsRes.data.requests || []);
      setUpdatesRemaining(requestsRes.data.updates_remaining || 0);
      setUpdatesTotal(requestsRes.data.updates_total || 0);
      setPackageName(requestsRes.data.package_name || "Kein Abo");
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const submitContentRequest = async () => {
    if (!requestDesc.trim()) {
      toast.error("Bitte beschreibe deine Aenderung");
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("auth_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/content-requests`, {
        page_id: pageId,
        request_type: requestType,
        description: requestDesc,
      }, { headers });
      toast.success("Request eingereicht!");
      setRequestDesc("");
      setShowRequestForm(false);
      setUpdatesRemaining(prev => Math.max(0, prev - 1));
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Fehler beim Einreichen";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchData(), fetchProvisioningStatus()]);
    setRefreshing(false);
    toast.success("Daten aktualisiert");
  };

  const authHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const toggleLeadProcessed = async (lead) => {
    try {
      const res = await axios.patch(`${API}/leads/item/${lead.id}/status`, { processed: lead.status !== "erledigt" }, { headers: authHeaders() });
      setLeads(prev => prev.map(l => (l.id === lead.id ? res.data : l)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status konnte nicht geändert werden");
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Anfrage von ${lead.name} endgültig löschen?`)) return;
    try {
      await axios.delete(`${API}/leads/item/${lead.id}`, { headers: authHeaders() });
      setLeads(prev => prev.filter(l => l.id !== lead.id));
      toast.success("Anfrage gelöscht");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem("auth_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/subscription/cancel`, { page_id: pageId }, { headers });
      setShowCancelConfirm(false);
      if (res.data.manual) {
        toast.success("Deine Kündigung ist eingegangen. Wir bestätigen sie dir persönlich.");
        setPage(prev => ({ ...prev, subscription: { ...prev.subscription, cancel_requested_at: new Date().toISOString() } }));
      } else {
        toast.success("Vertrag gekündigt. Deine Seite bleibt bis zum Laufzeitende aktiv.");
        setPage(prev => ({
          ...prev,
          subscription: {
            ...prev.subscription,
            cancel_at_period_end: true,
            current_period_end: res.data.current_period_end,
          },
        }));
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Kündigung fehlgeschlagen. Bitte versuche es erneut.";
      toast.error(msg);
    } finally {
      setCancelling(false);
    }
  };

  const getPublicUrl = () => {
    if (page?.custom_domain) return `https://${page.custom_domain}`;
    if (page?.subdomain) return `https://${page.subdomain}.webgenerator.io`;
    return `${process.env.REACT_APP_BACKEND_URL}/page/${pageId}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  const domainReady = provStatus?.all_done && provStatus?.live_verified;
  const showLiveBanner = domainReady && (page?.custom_domain || page?.subdomain);
  const showActivating = provStatus?.all_done && !provStatus?.live_verified && (page?.custom_domain || page?.subdomain);
  const showPipeline = provStatus && !provStatus.all_done && provStatus.provisioning && Object.keys(provStatus.provisioning).length > 0;

  const canBook = ["pro", "premium"].includes(page?.subscription?.package_id) && page?.subscription?.status === "active";
  const newLeads = leads.filter(l => l.status !== "erledigt").length;

  return (
    <DashboardShell page={page} section={section} setSection={setSection} newLeads={newLeads} canBook={canBook} packageName={packageName} onUpgrade={() => setUpgradeOpen(true)}
      onRefresh={handleRefresh} refreshing={refreshing} publicUrl={getPublicUrl()}>
        <div>
          {section === "overview" && (<>
          {/* Page Info + Subscription */}
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                {page?.company_name}
              </h1>
              <p className="text-slate-600">
                {page?.industry} in {page?.city}
              </p>
            </div>
            {packageName !== "Kein Abo" ? (
              <div className="flex items-center gap-3 bg-indigo-50 px-4 py-2 rounded-xl" data-testid="subscription-badge">
                <Package className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="text-sm font-semibold text-indigo-700">{packageName}-Paket</p>
                  <p className="text-xs text-indigo-500">{updatesRemaining}/{updatesTotal} Updates verfügbar</p>
                </div>
              </div>
            ) : (
              <Button
                data-testid="upgrade-btn"
                onClick={() => navigate(`/preise?page_id=${pageId}&industry=${encodeURIComponent(page?.industry || "")}`)}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                size="sm"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Abo waehlen
              </Button>
            )}
          </div>

          {page?.is_deactivated && (
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-5 mb-6 text-sm text-rose-800" data-testid="page-deactivated-banner">
              <strong>Deine Webseite ist derzeit deaktiviert.</strong> Besucher sehen einen Pausenhinweis. Bitte kontaktiere den Support unter support@webgenerator.io.
            </div>
          )}

          {showActivating && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 sm:p-6 mb-6 flex items-start gap-3" data-testid="domain-activating-banner">
              <Loader2 className="w-5 h-5 text-amber-600 animate-spin mt-0.5 flex-shrink-0" />
              <div>
                <h2 className="font-bold text-slate-900">Deine Domain wird aktiviert</h2>
                <p className="text-sm text-slate-600">Die Einrichtung ist abgeschlossen, {page?.custom_domain || page?.subdomain} ist aber noch nicht erreichbar. Das dauert meist wenige Minuten, in Einzelfällen bis zu 24 Stunden. Diese Anzeige aktualisiert sich automatisch.</p>
              </div>
            </div>
          )}

          {/* Live Banner */}
          {showLiveBanner && (
            <SiteLiveBanner 
              domainName={page?.custom_domain || page?.subdomain} 
              domainType={page?.domain_type || "subdomain"} 
            />
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-slate-100">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <span className="text-xs sm:text-sm text-slate-500">Besucher</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900">{stats.visitor_count}</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-slate-100">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                </div>
                <span className="text-xs sm:text-sm text-slate-500">Leads</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900">{stats.lead_count}</p>
            </div>

            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-slate-100">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                </div>
                <span className="text-xs sm:text-sm text-slate-500">Conversion</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900">
                {stats.visitor_count > 0 
                  ? `${((stats.lead_count / stats.visitor_count) * 100).toFixed(1)}%`
                  : "0%"}
              </p>
            </div>

            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-slate-100">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                </div>
                <span className="text-xs sm:text-sm text-slate-500">Status</span>
              </div>
              <p className="text-base sm:text-lg font-semibold text-green-600">
                {showActivating ? "Wird aktiviert" : page?.is_published ? "Online" : "Entwurf"}
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mb-8" data-testid="overview-quick-actions">
            {[
              ["website", "Bilder ändern", "Tausche Bilder deiner Seite direkt im Editor aus."],
              ["seo", "SEO-Check ansehen", "So gut wirst du bei Google und in KI-Suchen gefunden."],
              ["leads", "Anfragen ansehen", `${leads.length} Anfrage(n) über dein Kontaktformular.`],
            ].map(([id, t, d]) => (
              <button key={id} onClick={() => setSection(id)} data-testid={`quick-${id}`} className="text-left bg-white rounded-2xl border border-slate-100 shadow-sm p-5 hover:border-indigo-300 hover:shadow-md transition-all">
                <p className="font-semibold text-slate-900">{t}</p><p className="text-sm text-slate-500 mt-1">{d}</p>
              </button>
            ))}
          </div>
          </>)}

          {section === "website" && (<>
            <WebsiteEditor page={previewPage} pageId={pageId} onChanged={fetchData} />
            <h2 className="text-lg font-bold text-slate-900 mt-10 mb-4">Texte & größere Änderungen</h2>
          {/* Tab Content: Content Requests */}
          {(
            <div className="space-y-6">
              {/* New Request */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-900">Content-Update einreichen</h2>
                  <div className="flex items-center gap-3" data-testid="updates-counter">
                    <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-indigo-500 rounded-full transition-all" 
                        style={{ width: updatesTotal > 0 ? `${((updatesTotal - updatesRemaining) / updatesTotal) * 100}%` : '0%' }}
                      />
                    </div>
                    <span className="text-sm text-slate-500 whitespace-nowrap">
                      {updatesTotal - updatesRemaining} von {updatesTotal} genutzt
                    </span>
                  </div>
                </div>

                {updatesRemaining > 0 ? (
                  showRequestForm ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {REQUEST_TYPES.map(rt => {
                          const Icon = rt.icon;
                          return (
                            <button
                              key={rt.id}
                              data-testid={`request-type-${rt.id}`}
                              onClick={() => setRequestType(rt.id)}
                              className={`flex items-center gap-2 p-3 rounded-xl border text-sm transition-colors ${
                                requestType === rt.id 
                                  ? "border-indigo-500 bg-indigo-50 text-indigo-700" 
                                  : "border-slate-200 text-slate-600 hover:border-slate-300"
                              }`}
                            >
                              <Icon className="w-4 h-4" />
                              {rt.label}
                            </button>
                          );
                        })}
                      </div>
                      <textarea
                        data-testid="request-description"
                        value={requestDesc}
                        onChange={(e) => setRequestDesc(e.target.value)}
                        placeholder="Beschreibe deine gewünschte Änderung..."
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none resize-none text-sm"
                      />
                      <div className="flex gap-3">
                        <Button
                          data-testid="submit-request-btn"
                          onClick={submitContentRequest}
                          disabled={submitting || !requestDesc.trim()}
                          className="rounded-xl bg-indigo-600 hover:bg-indigo-700"
                        >
                          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                          Einreichen
                        </Button>
                        <Button variant="outline" onClick={() => { setShowRequestForm(false); setRequestDesc(""); }} className="rounded-xl">
                          Abbrechen
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      data-testid="new-request-btn"
                      onClick={() => setShowRequestForm(true)}
                      variant="outline"
                      className="w-full h-12 rounded-xl border-dashed border-2 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Neuen Request einreichen
                    </Button>
                  )
                ) : (
                  <div className="text-center py-4">
                    <p className="text-slate-500 text-sm mb-3">Keine Updates mehr verfügbar in diesem Monat.</p>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/preise?page_id=${pageId}&industry=${encodeURIComponent(page?.industry || "")}`)}
                      className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                    >
                      Upgrade für mehr Updates
                    </Button>
                  </div>
                )}
              </div>

              {/* Request History */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100">
                  <h2 className="text-lg font-semibold text-slate-900">Verlauf</h2>
                </div>
                {contentRequests.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                      <FileText className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="font-medium text-slate-900 mb-1">Noch keine Requests</h3>
                    <p className="text-sm text-slate-500">Reiche deinen ersten Content-Update ein.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {contentRequests.map((req) => {
                      const statusInfo = STATUS_MAP[req.status] || STATUS_MAP.open;
                      const TypeIcon = REQUEST_TYPES.find(t => t.id === req.request_type)?.icon || FileText;
                      return (
                        <div key={req.id} className="p-5 hover:bg-slate-50 transition-colors">
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <TypeIcon className="w-5 h-5 text-slate-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusInfo.color}`}>
                                  {statusInfo.label}
                                </span>
                                <span className="text-xs text-slate-400 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDate(req.created_at)}
                                </span>
                              </div>
                              <p className="text-sm text-slate-700">{req.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
          </>)}

          {section === "seo" && <SeoSection pageId={pageId} />}

          {/* Tab Content: Leads */}
          {section === "leads" && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <h2 className="text-lg font-semibold text-slate-900">Eingegangene Anfragen</h2>
                <p className="text-xs text-slate-500 mt-1" data-testid="lead-retention-info">
                  Datenschutz: Anfragen werden automatisch gelöscht – {retention.lead_processed_days} Tage nach „erledigt“, spätestens {retention.lead_max_days} Tage nach Eingang. Bitte sichere Anfragen, die du aus gesetzlichen Gründen aufbewahren musst.
                </p>
              </div>
              {leads.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                    <Users className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="font-medium text-slate-900 mb-1">Noch keine Anfragen</h3>
                  <p className="text-sm text-slate-500">
                    Teile deine Seite, um erste Leads zu erhalten.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {leads.map((lead) => (
                    <div key={lead.id} className="p-6 hover:bg-slate-50 transition-colors">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-slate-900">{lead.name}</h3>
                            <span className="text-xs text-slate-400">
                              {formatDate(lead.created_at)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                            {lead.contact.includes("@") ? <Mail className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
                            <a 
                              href={lead.contact.includes("@") ? `mailto:${lead.contact}` : `tel:${lead.contact}`}
                              className="text-indigo-600 hover:underline"
                            >
                              {lead.contact}
                            </a>
                          </div>
                          <p className="text-slate-600 text-sm bg-slate-50 p-3 rounded-lg">{lead.message}</p>
                          <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500" data-testid={`lead-retention-${lead.id}`}>
                            <span className={`px-2 py-0.5 rounded-full ${lead.status === "erledigt" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                              {lead.status === "erledigt" ? "Erledigt" : "Neu"}
                            </span>
                            {lead.delete_after && <span>Automatische Löschung am {new Date(lead.delete_after).toLocaleDateString("de-DE")}</span>}
                            <button onClick={() => toggleLeadProcessed(lead)} className="underline hover:text-slate-800" data-testid={`lead-toggle-${lead.id}`}>
                              {lead.status === "erledigt" ? "Wieder öffnen" : "Als erledigt markieren"}
                            </button>
                            <button onClick={() => deleteLead(lead)} className="underline text-red-600 hover:text-red-800" data-testid={`lead-delete-${lead.id}`}>Jetzt löschen</button>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            window.location.href = lead.contact.includes("@") ? `mailto:${lead.contact}` : `tel:${lead.contact}`;
                          }}
                          className="rounded-full"
                        >
                          {lead.contact.includes("@") ? <><Mail className="w-4 h-4 mr-2" />E-Mail</> : <><Phone className="w-4 h-4 mr-2" />Anrufen</>}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}


          {section === "contract" && (<>
          {!(provStatus?.provisioning && Object.keys(provStatus.provisioning).length > 0) && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6" data-testid="setup-path">
              <h2 className="font-bold text-slate-900 mb-1">Einrichtungspfad</h2>
              <p className="text-sm text-slate-500 mb-4">So weit ist die Einrichtung deiner Webseite.</p>
              <ol className="space-y-3">
                {[
                  ["Seite erstellt", true],
                  ["Paket gebucht", !!page?.subscription?.package_id],
                  ["Domain festgelegt", !!(page?.custom_domain || page?.subdomain)],
                  ["SSL-Zertifikat aktiv", page?.ssl_status === "active"],
                  ["Seite online", !!page?.is_published && page?.deployment_status === "active"],
                ].map(([label, done]) => (
                  <li key={label} className="flex items-center gap-3 text-sm" data-testid={`setup-step-${label}`}>
                    {done ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Clock className="w-5 h-5 text-slate-300" />}
                    <span className={done ? "text-slate-900" : "text-slate-400"}>{label}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {/* Provisioning Pipeline */}
          {provStatus?.provisioning && Object.keys(provStatus.provisioning).length > 0 && (
            <ProvisioningPipeline 
              provisioning={provStatus.provisioning}
              domainType={provStatus.domain_type}
              allDone={provStatus.all_done}
            />
          )}

          {/* Subscription Management */}
          {page?.subscription?.status === "active" && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 sm:p-6 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="subscription-management">
              {page.subscription.cancel_requested_at && !page.subscription.cancel_at_period_end ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Kündigung eingegangen</p>
                    <p className="text-sm text-slate-500" data-testid="cancellation-requested">
                      Wir bearbeiten deine Kündigung und bestätigen sie dir per E-Mail. Deine Seite bleibt bis dahin aktiv.
                    </p>
                  </div>
                </div>
              ) : page.subscription.cancel_at_period_end ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <XCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Vertrag gekündigt</p>
                    <p className="text-sm text-slate-500" data-testid="cancellation-date">
                      {page.subscription.current_period_end
                        ? `Deine Seite bleibt aktiv bis ${formatDate(page.subscription.current_period_end)}. Danach werden Domain, Hosting und Seite gelöscht.`
                        : "Deine Seite bleibt bis zum Ende der aktuellen Laufzeit aktiv."}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <p className="font-semibold text-slate-900">Vertrag verwalten</p>
                    <p className="text-sm text-slate-500">Du kannst dein Abo jederzeit zum Ende der Laufzeit kündigen.</p>
                  </div>
                  <Button
                    data-testid="cancel-subscription-btn"
                    variant="outline"
                    onClick={() => setShowCancelConfirm(true)}
                    className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    Vertrag kündigen
                  </Button>
                </>
              )}
            </div>
          )}

          <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
            <AlertDialogContent data-testid="cancel-subscription-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Vertrag wirklich kündigen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Deine Seite bleibt bis zum Ende der aktuell bezahlten Laufzeit erreichbar. Danach
                  werden Domain, Hosting und deine Webseite <strong>vollständig und unwiderruflich gelöscht</strong>.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cancel-subscription-dialog-cancel">Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="cancel-subscription-dialog-confirm"
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Ja, kündigen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <LegalDataCard page={page} pageId={pageId} onSaved={(f) => setPage(prev => ({ ...prev, ...f }))} />

          </>)}

          {section === "referrals" && <ReferralSection />}

          {section === "shop" && <ShopSection pageId={pageId} onUpgrade={() => setUpgradeOpen(true)} />}

          {section === "branding" && <BrandingSection pageId={pageId} isPremium={page?.subscription?.package_id === "premium" && page?.subscription?.status === "active"} onUpgrade={() => setUpgradeOpen(true)} onChanged={fetchData} />}

          {section === "support" && <BookingSection pageId={pageId} page={page} onUpgrade={() => setUpgradeOpen(true)} />}
        </div>
      <UpgradeDialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} pageId={pageId} onUpgraded={fetchData} />
    </DashboardShell>
  );
}
